import React from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { Button } from "ds/Button";
import * as Code from "ds/Code";
import getUrl from "Libs/getUrl";
import { capitalize, goToEnvironment } from "Libs/utils";
import { merge } from "Reducers/environment/actions/merge";
import { useAppDispatch } from "Store/hooks";

import * as S from "./MergePane.styles";

import type { Environment } from "@packages/client";

type MergeContentProps = {
  parent?: Environment;
  environment?: Environment;
  organizationId: string;
  projectId: string;
  environmentId: string;
  isLoading?: boolean;
  cliDialog: React.ReactNode;
  isSizingApiEnabled?: boolean;
};
const MergeContent = ({
  parent,
  environment,
  organizationId,
  projectId,
  environmentId,
  isLoading,
  cliDialog,
  isSizingApiEnabled
}: MergeContentProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const command = intl.formatMessage(
    { id: "merge.command" },
    {
      commandName: CLI_COMMAND_NAME,
      action: environment?.id
    }
  );

  const handleMerge = async () => {
    const response = await dispatch(
      merge({ organizationId, projectId, environmentId })
    );

    if (response?.payload?.parameters) {
      const environmentOverviewUrl = getUrl({
        key: "organization.project.environment",
        props: {
          organizationId,
          projectId,
          environmentId
        }
      });
      navigate(environmentOverviewUrl);
    }
  };

  return (
    <>
      <Typography.BodyText size="medium" weight="regular">
        {intl.formatMessage(
          { id: "merge.question" },
          {
            parentEnvironment: (
              <S.SimpleBodyText size="medium" weight="semiBold">
                {parent?.title}
              </S.SimpleBodyText>
            ),
            currentEnvironment: (
              <S.SimpleBodyText size="medium" weight="semiBold">
                {environment?.title}
              </S.SimpleBodyText>
            )
          }
        )}
      </Typography.BodyText>

      <S.Separator />
      <S.CliDialogWrapper>
        <Typography.Label textStyle="default">
          {intl.formatMessage({
            id: `merge.cli${isSizingApiEnabled ? ".sizing_api_enabled" : ""}`
          })}
        </Typography.Label>
        {cliDialog}
      </S.CliDialogWrapper>
      <Code.Block isCopyable singleLine id={command}>
        {command}
      </Code.Block>

      {isLoading ? (
        <S.LoadingSuccess />
      ) : (
        <S.InnerButtonWrapper>
          <Button
            analyticId="cancel"
            id="environment-merge-cancel-btn"
            variant="secondary"
            onClick={() =>
              goToEnvironment(
                navigate,
                organizationId,
                projectId,
                environmentId
              )
            }
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
          <Button
            analyticId="merge.button"
            id="environment-merge-save-btn"
            type="submit"
            onClick={handleMerge}
          >
            {capitalize(intl.formatMessage({ id: "merge.button" }))}
          </Button>
        </S.InnerButtonWrapper>
      )}
    </>
  );
};

export default MergeContent;
